import { getOrganizationRoleSummary } from './api/userRole';
import { getUserId, isBrowser } from './browserContext';
import {
    BRAZE_API_KEY,
    BRAZE_BASE_URL,
    CURRENT_ENV,
    ENVIRONMENT,
} from './config';

export type BrazeWebSDK = typeof import('@braze/web-sdk');

let braze: BrazeWebSDK | null = null;
let currentUserId: string | null = null;

function splitExternalUserId(externalUserId: string): {
    organizationId: string;
    publicUserId: string;
} {
    // `externalUserId` is `${organizationId}-${publicUserId}`.
    // orgId may contain '-' in some contexts; userId is always at the end.
    const lastDashIndex = externalUserId.lastIndexOf('-');
    return {
        organizationId:
            lastDashIndex > 0
                ? externalUserId.slice(0, lastDashIndex)
                : externalUserId,
        publicUserId:
            lastDashIndex > 0 ? externalUserId.slice(lastDashIndex + 1) : '',
    };
}

async function getUserRole(
    organizationId: string,
    publicUserId: string,
): Promise<string | null> {
    return publicUserId
        ? await getOrganizationRoleSummary(organizationId, publicUserId)
        : null;
}

async function setBrazeUserAttributes(instance: BrazeWebSDK): Promise<void> {
    const user = await instance.getUser();
    const externalUserId = getUserId();
    const { email, tld } = getUserAttributes() as {
        email?: string;
        tld?: string;
    };

    const { organizationId, publicUserId } =
        splitExternalUserId(externalUserId);

    const role = await getUserRole(organizationId, publicUserId);

    user?.setCustomUserAttribute('EMAIL', email ?? null);
    user?.setCustomUserAttribute('EXTERNAL_USER_ID', externalUserId);
    user?.setCustomUserAttribute('CURRENT_TLD', tld ?? null);
    user?.setCustomUserAttribute('USER_ROLE', role ?? null);
}

export async function initBraze(): Promise<void> {
    if (!isBrowser()) return;

    try {
        const userId = getUserId();

        // If already initialized for this user, skip re-init
        if (currentUserId === userId) {
            return;
        }

        await loadAndConfigureBrazeForUser(userId); // If not yet initialized for any user, initialize the SDK
    } catch (error) {
        console.error('[Braze] Failed to initialize:', error);
        throw error;
    }
}

async function loadAndConfigureBrazeForUser(userId: string): Promise<void> {
    if (!braze) {
        braze = await import('@braze/web-sdk');
    }

    if (!currentUserId) {
        braze.initialize(BRAZE_API_KEY, {
            baseUrl: BRAZE_BASE_URL,
            enableLogging: CURRENT_ENV === ENVIRONMENT.DEVELOPMENT,
            allowUserSuppliedJavascript: true,
            enableSdkAuthentication: CURRENT_ENV === ENVIRONMENT.PRODUCTION,
            openCardsInNewTab: true,
        });
    }

    braze.changeUser(userId);
    braze.openSession();

    currentUserId = userId;
}

export const getUserAttributes = () => {
    const serverData = (window as any).__SERVER_DATA__;
    if (!isBrowser() || !serverData) {
        return {};
    }
    return {
        email: serverData.user?.email,
        tld: serverData.env?.localeInfo?.tld,
    };
};

export async function getBrazeInstance(): Promise<BrazeWebSDK> {
    // Capture initialization state before calling `initBraze()`.
    // TypeScript narrows `braze` to `null` inside `if (!braze) { ... }`, and it
    // won't infer that `initBraze()` mutates the module singleton.
    const shouldSetAttributes = !braze;

    if (shouldSetAttributes) {
        try {
            await initBraze();
            const instance = braze;
            if (!instance) {
                throw new Error(
                    '[Braze] SDK not initialized. Please try again later.',
                );
            }
            await setBrazeUserAttributes(instance);
        } catch (error) {
            console.error('[Braze] Failed to initialize:', error);
            throw new Error(
                '[Braze] SDK failed to initialize. Please try again later.',
            );
        }
    }

    if (!braze) {
        throw new Error('[Braze] SDK not initialized. Please try again later.');
    }

    return braze;
}
